import { useEffect, useRef, type ReactNode } from "react";
import { isTopmostModal, popModal, pushModal } from "../lib/modalStack.js";

/**
 * 基础模态框：焦点陷阱 + Esc 关闭 + 关闭后焦点归还 + 背景滚动锁定。
 *
 * 无障碍要点（不是可选项）：
 *   - role="dialog" + aria-modal="true"，打开即移焦进面板；
 *   - Tab / Shift+Tab 在面板内循环，不会跑到背后的表格；
 *   - 关闭后把焦点还给触发元素，否则键盘用户会「丢失位置」。
 *
 * 2026-10-04 第三轮：新增 `size`（用户第 4 条「设置改成一个大弹窗」）。
 * `md` 是原有尺寸（确认框 / 预演框都在用，**默认值不放宽**）；
 * `lg` 是「大弹窗」= 视口 86vh × 88vw，并以上限 `--content-max-width` 收口
 * （与页面内容区同一个宽度口径，见 `lib/tableLayout.ts`），避免超宽屏上
 * 弹窗比内容还宽、表头与输入框被拉得看不见关系。
 */

export type ModalSize = "md" | "lg";

export interface ModalProps {
  readonly open: boolean;
  readonly title: string;
  readonly description: string | null;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer: ReactNode;
  /** `md`（默认，原有尺寸）/ `lg`（大弹窗，占视口 86vh × 88vw）。 */
  readonly size?: ModalSize;
  /**
   * ⭐ 2026-10-09（用户："增加到下一个产品系列的快捷按钮"）：**标题栏右侧的附加控件**
   * （渲染在 `Esc` 左边）。不传 ⇒ 与既有逐字相同（老调用方零影响）。
   * ⛔ 只放"和这个弹窗的当前对象直接相关"的导航/切换控件，不要塞业务表单。
   */
  readonly headerExtra?: ReactNode;
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * 面板尺寸 class（纯映射，便于测试断言「设置弹窗真的是大弹窗」）。
 *
 * ⚠️ `lg` 必须显式 `max-w-none` 之外的**收口**：`w-[88vw]` 是「占视口 88%」，
 * `max-w-[var(--content-max-width)]` 是「不超过页面内容区宽度」，两者取小。
 */
const SIZE_CLASS: Readonly<Record<ModalSize, string>> = {
  md: "max-h-[85vh] w-full max-w-2xl",
  lg: "h-[86vh] w-[88vw] max-w-[var(--content-max-width)]",
};

export function modalPanelClass(size: ModalSize = "md", extra = ""): string {
  return `relative z-[var(--z-modal)] flex animate-panel-in flex-col overflow-hidden rounded-xl border border-line bg-raised elev-xl ${SIZE_CLASS[size]} ${extra}`.trim();
}

export function Modal({ open, title, description, onClose, children, footer, size = "md", headerExtra }: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** ⭐ 本层弹窗的栈令牌（JSX 里的遮罩点击也要用 ⇒ 放在组件作用域）。 */
  const tokenRef = useRef<symbol | null>(null);


  useEffect(() => {
    if (!open) return;
    // ⭐ 入栈：只有**栈顶**才响应 `Esc` / 遮罩（嵌套时**不再两层同关**）。
    const token = pushModal();
    tokenRef.current = token;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    panel?.focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        // ⛔ 非栈顶 ⇒ **什么都不做**（外层不会被内层的 Esc 带走）。
        if (!isTopmostModal(token)) return;
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab" || panel === null) return;
      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      // ⭐ 出栈（按令牌移除 ⇒ 卸载非栈顶也不会把栈顶误删）。
      popModal(token);
      tokenRef.current = null;
      document.removeEventListener("keydown", handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      previous?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-6">
      <div
        className="absolute inset-0 animate-overlay-in bg-overlay"
        onClick={() => {
          // ⭐ 遮罩同样只由**栈顶**响应（防"点内层遮罩把外层也关了"）。
          const token = tokenRef.current;
          if (token !== null && isTopmostModal(token)) onClose();
        }}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={modalPanelClass(size)}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line-subtle px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-ink">{title}</h2>
            {description === null ? null : (
              <p className="mt-1 text-meta text-ink-2">{description}</p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {headerExtra}
            <button
              type="button"
              onClick={onClose}
              aria-label="关闭"
              className="shrink-0 rounded-control border border-line px-2 py-1 text-meta text-ink-2 transition-colors dur-fast hover:bg-hover hover:text-ink"
            >
              Esc
            </button>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">{children}</div>
        <footer className="flex items-center justify-end gap-2 border-t border-line-subtle bg-sunken px-5 py-3">
          {footer}
        </footer>
      </div>
    </div>
  );
}
